import { useState } from "react";
import { Alert, AlertTitle, Box, Button } from "@mui/material";
import { IVacationAwardWarning } from "@vmosolutions/oc-interfaces";

import { VACATION_BID_TEST_ID } from "../testIds";
import { warningLines, warningTitle } from "./awardingHelpers";

export interface IAwardWarningsProps {
  warnings: IVacationAwardWarning[];
  /** Resolves true once the warning is gone. */
  onDismiss: (warningId: string) => Promise<boolean>;
}

const AwardWarningsStyles = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  marginTop: "8px",
  "& .MuiAlert-root": { padding: "2px 10px", fontSize: 12 },
  "& .MuiAlertTitle-root": { fontSize: 12, fontWeight: 700, marginBottom: "2px" },
  "& .MuiAlert-action .MuiButtonBase-root": { padding: "1px 8px", fontSize: 12 },
};

/** What entitlement changes did to a crew member's awards and left to do, each kept until the scheduler dismisses it. */
export const AwardWarnings = (props: IAwardWarningsProps): JSX.Element | null => {
  const { warnings, onDismiss } = props;
  const [dismissing, setDismissing] = useState<Set<string>>(() => new Set());

  const dismiss = async (warningId: string): Promise<void> => {
    setDismissing((current) => new Set(current).add(warningId));
    try {
      await onDismiss(warningId);
    } finally {
      setDismissing((current) => {
        const next = new Set(current);
        next.delete(warningId);
        return next;
      });
    }
  };

  if (!warnings.length) return null;
  return (
    <Box sx={AwardWarningsStyles}>
      {warnings.map((warning) => {
        const id = String(warning._id);
        return (
          <Alert
            key={id}
            severity={warning.actions.length ? "warning" : "info"}
            data-testid={VACATION_BID_TEST_ID.awardWarning}
            action={
              <Button
                size="small"
                color="inherit"
                disabled={dismissing.has(id)}
                onClick={() => void dismiss(id)}
                data-testid={VACATION_BID_TEST_ID.dismissWarningButton}
              >
                Dismiss
              </Button>
            }
          >
            <AlertTitle>{warningTitle(warning)}</AlertTitle>
            {warningLines(warning).map((line) => (
              <div key={line}>{line}</div>
            ))}
          </Alert>
        );
      })}
    </Box>
  );
};

export default AwardWarnings;
